import { create } from "zustand";
import type { OfferingType, Palette, Petition, SanctumState, SigilDesign } from "../types";

const now = () => Date.now();
const id = () => crypto.randomUUID();

export function freshEmptyState(): SanctumState {
  return {
    version: 1,
    updatedAt: 0,
    shrine: {
      palette: "blanca",
      offerings: [],
      petitions: [],
      answered: [],
      audio: { enabled: false, master: 0.35, wick: true, copal: true, drone: true },
    },
    grimoire: [],
    ritualLog: [],
  };
}

export const EMPTY_STATE: SanctumState = freshEmptyState();

function takeLegacyPlaintextCache(): SanctumState | null {
  if (typeof localStorage === "undefined") return null;
  const storageKey = "st-pepsi-quiet-archive-v1";
  try {
    const raw = localStorage.getItem(storageKey);
    // The old plaintext copy is removed before any account or network work. A
    // valid legacy draft stays hidden in memory until the owner signs in, then
    // it is encrypted by the next account sync.
    localStorage.removeItem(storageKey);
    if (!raw) return null;
    const candidate = JSON.parse(raw)?.state;
    if (candidate?.version !== 1 || !candidate?.shrine || typeof candidate.shrine !== "object") return null;
    const empty = freshEmptyState();
    return {
      version: 1,
      updatedAt: Number.isFinite(Number(candidate.updatedAt)) ? Number(candidate.updatedAt) : 0,
      shrine: {
        ...empty.shrine,
        ...candidate.shrine,
        offerings: Array.isArray(candidate.shrine.offerings) ? candidate.shrine.offerings.slice(-40) : [],
        petitions: Array.isArray(candidate.shrine.petitions) ? candidate.shrine.petitions.slice(-60) : [],
        answered: Array.isArray(candidate.shrine.answered) ? candidate.shrine.answered.slice(-120) : [],
        audio: { ...empty.shrine.audio, ...(candidate.shrine.audio || {}) },
      },
      grimoire: Array.isArray(candidate.grimoire) ? candidate.grimoire.slice(-120) : [],
      ritualLog: Array.isArray(candidate.ritualLog) ? candidate.ritualLog.slice(-240) : [],
    } as SanctumState;
  } catch {
    return null;
  }
}

let pendingLegacyState = takeLegacyPlaintextCache();

export function consumePendingLegacyState(): SanctumState | null {
  const state = pendingLegacyState;
  pendingLegacyState = null;
  return state;
}

interface SanctumActions {
  applyRemote: (state: SanctumState) => void;
  resetPrivateState: () => void;
  setPalette: (palette: Palette) => void;
  addOffering: (type: OfferingType, position?: { x: number; y: number }) => void;
  updateOffering: (offeringId: string, change: "refill" | "light" | "puff" | "remove") => void;
  addPetition: (title: string, text: string) => string;
  enrichPetition: (petitionId: string, value: Partial<Pick<Petition, "prayer" | "reflection" | "palette">>) => void;
  burnPetition: (petitionId: string) => void;
  setAudio: (value: Partial<SanctumState["shrine"]["audio"]>) => void;
  saveSigil: (sigil: SigilDesign) => void;
  deleteSigil: (sigilId: string) => void;
  logSigilCharge: (sigilId: string, method: "gnosis" | "fire") => void;
  addRitualLog: (type: SanctumState["ritualLog"][number]["type"], label: string) => void;
}

export type SanctumStore = SanctumState & SanctumActions;

function withLog(state: SanctumStore, type: SanctumState["ritualLog"][number]["type"], label: string) {
  return [...state.ritualLog, { id: id(), type, label: label.slice(0, 180), at: now() }].slice(-240);
}

export const useSanctumStore = create<SanctumStore>()((set) => ({
  ...freshEmptyState(),
  applyRemote: (state) => set({ ...state }),
  resetPrivateState: () => set(freshEmptyState()),
  setPalette: (palette) => set((state) => ({
    shrine: { ...state.shrine, palette },
    updatedAt: now(),
  })),
  addOffering: (type, position) => set((state) => {
    const placedAt = now();
    const labels: Record<OfferingType, string> = {
      water: "Fresh water placed", candle: "Votive candle placed", incense: "Copal set to burn",
      bread: "Pan de Muerto offered", marigolds: "Marigolds offered", tequila: "Tequila offered",
      cigar: "Cigar offered", coins: "Coins offered",
    };
    return {
      shrine: {
        ...state.shrine,
        offerings: [...state.shrine.offerings, {
          id: id(), type,
          x: position?.x ?? 0.2 + Math.random() * 0.6,
          y: position?.y ?? 0.78 + Math.random() * 0.1,
          placedAt,
          expiresAt: placedAt + (type === "water" ? 3 : 7) * 24 * 60 * 60 * 1000,
          burnEndsAt: 0,
          lit: false,
        }].slice(-40),
      },
      ritualLog: withLog(state, "offering", labels[type]),
      updatedAt: placedAt,
    };
  }),
  updateOffering: (offeringId, change) => set((state) => {
    if (change === "remove") return {
      shrine: { ...state.shrine, offerings: state.shrine.offerings.filter((item) => item.id !== offeringId) },
      updatedAt: now(),
    };
    return {
      shrine: {
        ...state.shrine,
        offerings: state.shrine.offerings.map((item) => {
          if (item.id !== offeringId) return item;
          if (change === "refill" && item.type === "water") {
            return { ...item, placedAt: now(), expiresAt: now() + 3 * 24 * 60 * 60 * 1000 };
          }
          if (change === "light" && item.type === "candle") {
            return { ...item, lit: true, burnEndsAt: now() + 4 * 60 * 60 * 1000 };
          }
          return { ...item, placedAt: now() };
        }),
      },
      updatedAt: now(),
    };
  }),
  addPetition: (title, text) => {
    const petitionId = id();
    set((state) => ({
      shrine: { ...state.shrine, petitions: [...state.shrine.petitions, {
        id: petitionId, title: title.trim().slice(0, 100) || "Private petition",
        text: text.trim().slice(0, 4000), createdAt: now(), prayer: "", reflection: "",
        palette: state.shrine.palette, status: "active" as const, burnedAt: 0,
      }].slice(-60) },
      ritualLog: withLog(state, "petition", "A private petition was written"),
      updatedAt: now(),
    }));
    return petitionId;
  },
  enrichPetition: (petitionId, value) => set((state) => ({
    shrine: { ...state.shrine, petitions: state.shrine.petitions.map((item) => item.id === petitionId ? { ...item, ...value } : item) },
    updatedAt: now(),
  })),
  burnPetition: (petitionId) => set((state) => {
    const petition = state.shrine.petitions.find((item) => item.id === petitionId);
    if (!petition) return state;
    return {
      shrine: {
        ...state.shrine,
        petitions: state.shrine.petitions.filter((item) => item.id !== petitionId),
        answered: [...state.shrine.answered, { ...petition, status: "archived" as const, burnedAt: now() }].slice(-120),
      },
      ritualLog: withLog(state, "petition", `Petition archived: ${petition.title}`),
      updatedAt: now(),
    };
  }),
  setAudio: (value) => set((state) => ({
    shrine: { ...state.shrine, audio: { ...state.shrine.audio, ...value } },
    updatedAt: now(),
  })),
  saveSigil: (sigil) => set((state) => ({
    grimoire: [...state.grimoire.filter((item) => item.id !== sigil.id), sigil].slice(-120),
    ritualLog: withLog(state, "save", `Sigil saved: ${sigil.reduced}`),
    updatedAt: now(),
  })),
  deleteSigil: (sigilId) => set((state) => ({
    grimoire: state.grimoire.filter((item) => item.id !== sigilId),
    updatedAt: now(),
  })),
  logSigilCharge: (sigilId, method) => set((state) => ({
    grimoire: state.grimoire.map((item) => item.id === sigilId ? {
      ...item, charges: [...item.charges, { at: now(), method, energy: 100 }].slice(-40),
    } : item),
    ritualLog: withLog(state, method === "fire" ? "banish" : "charge", method === "fire" ? "A sigil was disintegrated" : "A sigil reached full charge"),
    updatedAt: now(),
  })),
  addRitualLog: (type, label) => set((state) => ({
    ritualLog: withLog(state, type, label), updatedAt: now(),
  })),
}));

export function sanctumSnapshot(): SanctumState {
  const state = useSanctumStore.getState();
  return {
    version: 1,
    updatedAt: state.updatedAt,
    shrine: state.shrine,
    grimoire: state.grimoire,
    ritualLog: state.ritualLog,
  };
}
